import CalendarIcon from "@mui/icons-material/CalendarToday";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import { alpha, Chip, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GridColDef } from "@mui/x-data-grid-premium";
import { SuggestedChip } from "components/ArtificialIntelligence/SuggestedChip";
import { HoverAction } from "components/HoverAction";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SuggestedTasksQuery, Frequency } from "generated-graphql/graphql";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo, useState } from "react";
import { prettyPrintDateMed } from "util/dates";

import { SUGGESTED_TASKS_QUERY } from "./schema";
import { SuggestedTaskDialog } from "./SuggestedTaskDialog";
import { useHandleSuggestedTasks } from "./useHandleSuggestedTasks";

type SuggestedTask = SuggestedTasksQuery["suggestedTasks"]["items"][0];

interface SuggestedTasksGridProps {
  tenantId: string;
}

const getRecurrenceLabel = (frequency: Frequency, interval: number) => {
  if (frequency === Frequency.Once) {
    return "Does not repeat";
  }
  const frequencyLabel = prettyPrintEnumValue(frequency).toLowerCase();
  if (interval === 1) {
    return frequencyLabel.charAt(0).toUpperCase() + frequencyLabel.slice(1);
  }
  return `Every ${interval} ${frequencyLabel.replace("ly", "")}s`;
};

export const SuggestedTasksGrid = ({ tenantId }: SuggestedTasksGridProps) => {
  const theme = useTheme();
  const [selectedTask, setSelectedTask] = useState<SuggestedTask | null>(null);

  const { pushIdToUrl } = useSelectedIdInUrl("virtualEventId");

  const {
    acceptTask,
    dismissTask,
    editTask,
    isTaskRecentlyAccepted,
    loading: mutationLoading,
  } = useHandleSuggestedTasks({ tenantId });

  const columns: GridColDef<SuggestedTask>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
        renderCell: ({ row }) => <Typography variant="body2">{row.title}</Typography>,
      },
      {
        field: "documents",
        headerName: "Associated Documents",
        flex: 1,
        renderCell: ({ row }) =>
          row.documents.length > 0 ? (
            <Tooltip title={row.documents.map((d) => d.title).join(", ")}>
              <Typography
                variant="body2"
                noWrap
                sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {row.documents.length === 1
                  ? row.documents[0].title
                  : `${row.documents.length} documents`}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "facilities",
        headerName: "Facilities",
        flex: 1,
        renderCell: ({ row }) =>
          row.facilities.length > 0 ? (
            <Tooltip title={row.facilities.map((f) => f.name).join(", ")}>
              <Typography
                variant="body2"
                noWrap
                sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {row.facilities.length === 1
                  ? row.facilities[0].name
                  : `${row.facilities.length} facilities`}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "startDate",
        headerName: "Due Date",
        sortable: false,
        filterKeyType: "date",
        flex: 0.7,
        renderCell: ({ row }) => (
          <Typography variant="body2">{prettyPrintDateMed(row.startDate)}</Typography>
        ),
      },
      {
        field: "frequency",
        headerName: "Frequency",
        sortable: false,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(Frequency),
        enumPresentationFunction: (enumValue: Frequency) => {
          if (enumValue === Frequency.Once) {
            return "Does not repeat";
          }
          return prettyPrintEnumValue(enumValue);
        },
        flex: 0.8,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            {getRecurrenceLabel(row.frequency, row.interval)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: false,
        filterable: false,
        flex: 0.6,
        renderCell: ({ row }) => {
          // If the task has not been accepted yet, show the suggestion chip
          if (!isTaskRecentlyAccepted(row.id)) {
            return (
              <SuggestedChip
                label="Suggestion"
                size="small"
                isSuggested={true}
                hideAcceptButton={true}
                hideRejectButton={true}
              />
            );
          }
          // If the task has been accepted, then check the date
          if (new Date(row.startDate) >= new Date()) {
            return (
              <Chip
                label="Open"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.light, 0.1),
                  color: theme.palette.primary.main,
                }}
              />
            );
          } else {
            return (
              <Chip
                label="Overdue"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.error.light, 0.1),
                  color: theme.palette.error.main,
                }}
              />
            );
          }
          return null;
        },
      },
      {
        field: "actions",
        type: "actions",
        sortable: false,
        filterable: false,
        flex: 0.5,
        getActions: ({ row }) =>
          !isTaskRecentlyAccepted(row.id)
            ? [
                <Tooltip title="Dismiss" key="dismiss">
                  <HoverAction
                    label="Dismiss suggestion"
                    icon={<Close fontSize="small" />}
                    onClick={() => dismissTask(row)}
                  />
                </Tooltip>,
                <Tooltip title="Accept" key="accept">
                  <HoverAction
                    label="Accept suggestion"
                    icon={<Check fontSize="small" color="primary" />}
                    onClick={() => acceptTask(row)}
                  />
                </Tooltip>,
              ]
            : [],
      },
    ],
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [acceptTask, dismissTask, isTaskRecentlyAccepted]
  );

  // Note: Design does not call for suggested tasks to be shown in a calendar view
  // But, it feels better to have this here for consistency
  const disabledViewModeToggler = [
    <ToggleButtonGroup
      key="view-mode-toggle-button-group"
      value="list"
      exclusive
      color="primary"
      size="large"
      disabled
    >
      Omnisearch
      <ToggleButton value="list">
        <ListIcon />
      </ToggleButton>
      <ToggleButton value="calendar">
        <CalendarIcon />
      </ToggleButton>
    </ToggleButtonGroup>,
  ];

  return (
    <>
      <OmnisearchDataGrid
        persistenceKey="suggested-tasks-grid"
        columns={columns}
        dataQuery={SUGGESTED_TASKS_QUERY}
        defaultSearch={`tenantId:${tenantId}`}
        getItems={(data) => data.suggestedTasks.items}
        getCount={(data) => data.suggestedTasks.count ?? 0}
        onRowClick={({ row }) =>
          isTaskRecentlyAccepted(row.id)
            ? pushIdToUrl(`${row.id}_${row.startDate}`)
            : setSelectedTask(row)
        }
        startCommandButtons={disabledViewModeToggler}
        showManageColumns={false}
      />
      <SuggestedTaskDialog
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        onAccept={(task) => {
          editTask({
            ...task,
            isSuggested: false,
          });
          setSelectedTask(null);
        }}
        onDismiss={(task) => {
          dismissTask(task);
          setSelectedTask(null);
        }}
        loading={mutationLoading}
        readonly={isTaskRecentlyAccepted(selectedTask?.id ?? "")}
      />
    </>
  );
};
